import {Injectable, OnDestroy} from "@angular/core";
import {FUIAlertService} from "@fedeltapointofsale/ui";
import {ReportModel, ReportRequestData, ReportService} from "@fedeltapointofsale/data-client";
import {BehaviorSubject, first, Observable, Subject, Subscription, timer} from "rxjs";
import * as FileSaver from "file-saver";
import {AnalyticEvent} from "../../../analytics/analytic-event";
import {AnalyticsService} from "../../../analytics/analytics.service";

export class ReportDownloaderReg {

    constructor(public reportDownloader: ReportDownloader, public changeSubscription: Subscription) {
    }
}

/**
 * Handles generation and download of a report.
 */
@Injectable()
export class ReportGenerationService implements OnDestroy {

    private reportDownloaderRegs: ReportDownloaderReg[] = [];

    running$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(private reportService: ReportService,
                private analyticsService: AnalyticsService,
                private alertService: FUIAlertService) {
    }

    ngOnDestroy(): void {
        this.reportDownloaderRegs.forEach((reportDownloaderReg) => {
            reportDownloaderReg.reportDownloader.release();
            reportDownloaderReg.changeSubscription.unsubscribe();
        });
    }

    /**
     * Generate and download a report for a model
     */
    public execute(reportModel: ReportModel<any>): Promise<Boolean> {
        return reportModel.requestReport().then(reportRequest => {
            this.analyticsService.trackEvent(AnalyticEvent.GENERATE_REPORT, reportModel);
            if (reportRequest.status === "DUPLICATE") {
                this.alertService.showError(undefined, "wbo.common.alert.reportDuplicate").subscribe(() => {});
            } else {
                const reportDownloader: ReportDownloader = new ReportDownloader(this.reportService, reportRequest, this.alertService);
                const changeSubscription: Subscription = reportDownloader.changeStatus$.subscribe(() => this.evaluateDownloaders());
                this.reportDownloaderRegs.push(new ReportDownloaderReg(reportDownloader, changeSubscription));
                this.evaluateDownloaders();
            }
            return true;
        });
    }

    evaluateDownloaders(): void {
        let running: boolean = false;
        this.reportDownloaderRegs = this.reportDownloaderRegs.filter((reportDownloaderReg) => {
            if (reportDownloaderReg.reportDownloader.running) {
                running = true;
                return true;
            } else {
                reportDownloaderReg.changeSubscription.unsubscribe();
                reportDownloaderReg.reportDownloader.release();
                return false;
            }
        });
        this.running$.next(running);
    }

    get running(): boolean {
        return this.running$.getValue();
    }
}

/**
 * Start the download of a report by checking its status and when complete
 */
class ReportDownloader {
    private subscription: Subscription;
    running: boolean;

    changeStatus$: Subject<void> = new Subject<void>();

    constructor(private reportService: ReportService, private reportRequest: ReportRequestData, private alertService: FUIAlertService) {
        this.initNextPool();
        this.running = true;
    }

    initNextPool(): void {
        this.subscription = timer(3000).pipe(first()).subscribe(() => this.checkAndDownload());
    }

    checkAndDownload(): void {
        if (this.running) {
            this.reportService.getReportRequest(this.reportRequest.identifier).then((reportRequest) => {
                this.reportRequest = reportRequest;

                if (this.reportRequest.status === ReportRequestData.STATUS_REQUESTED) {
                    this.initNextPool();
                } else if (this.reportRequest.status === ReportRequestData.STATUS_COMPLETE) {
                    this.reportService.getReportStream(this.reportRequest.identifier).then((blob) => {
                        const currentDate: Date = new Date();
                        FileSaver.saveAs(blob, this.reportRequest.report + "-" + currentDate.toDateString() + "." + (this.reportRequest.contentType || "pdf"));
                        this.release();
                    }).catch((error) => {
                        this.release();
                        return Promise.reject(error);
                    });
                } else {
                    this.release();
                    if (this.reportRequest.status === ReportRequestData.STATUS_FAILED) {
                        this.alertService.showError(undefined, "wbo.common.alert.reportFailed").subscribe(() => {});
                    } else if (this.reportRequest.status === ReportRequestData.STATUS_NOPAGES) {
                        this.alertService.showError(undefined, "wbo.common.alert.reportNoPages").subscribe(() => {});
                    }
                }
            }
            ).catch((error) => {
                this.release();
                return Promise.reject(error);
            });
        }
    }

    release(): void {
        this.running = false;
        this.subscription.unsubscribe();
        this.changeStatus$.next();
    }
}
